import React, { useEffect, useMemo, useState } from "react";
import api, { authFetch } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Download, Trash2, Search, Palmtree, FileSignature, HeartHandshake,
         MoreHorizontal, Stethoscope, ChevronLeft, ChevronRight, CalendarDays,
         Edit, History, AlertTriangle, ArrowUp, ArrowDown, ArrowUpDown, FileSpreadsheet } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import CampoFirma from "@/components/CampoFirma";

const TYPE_META = {
  ferie: { label: "Ferie", icon: Palmtree, color: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  permesso: { label: "Permesso", icon: FileSignature, color: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  permesso_104: { label: "Permesso Legge 104", icon: HeartHandshake, color: "bg-purple-100 text-purple-700", dot: "bg-purple-500" },
  malattia: { label: "Malattia", icon: Stethoscope, color: "bg-red-100 text-red-700", dot: "bg-red-500" },
  altro: { label: "Altro", icon: MoreHorizontal, color: "bg-slate-100 text-slate-700", dot: "bg-slate-400" },
};

export default function FeriePermessi() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editRichiesta, setEditRichiesta] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [search, setSearch] = useState("");
  const [summary, setSummary] = useState(null);
  const [annoRiepilogo, setAnnoRiepilogo] = useState(String(new Date().getFullYear()));

  const load = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterType !== "all") params.append("leave_type", filterType);
      const { data } = await api.get(`/leave-requests?${params.toString()}`);
      setList(data);
    } catch { toast.error("Errore caricamento richieste"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filterType]);
  useEffect(() => {
    api.get(`/leave-requests/summary?year=${annoRiepilogo}`).then(({ data }) => setSummary(data)).catch(() => {});
  }, [list, annoRiepilogo]);

  const del = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa richiesta?")) return;
    try { await api.delete(`/leave-requests/${id}`); toast.success("Richiesta eliminata"); load(); }
    catch { toast.error("Errore"); }
  };

  const downloadPdf = async (req) => {
    try {
      const r = await authFetch(`/leave-requests/${req.id}/pdf`);
      if (!r.ok) throw new Error();
      const b = await r.blob();
      const u = window.URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = u; a.download = `richiesta_${req.employee_name.replace(" ", "_")}_${req.date_from}.pdf`; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Errore nel download del PDF"); }
  };

  const q = search.trim().toLowerCase();
  const filtered = list.filter(r => !q || r.employee_name.toLowerCase().includes(q));

  return (
    <div className="space-y-6" data-testid="ferie-permessi-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Dipendenti</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Ferie e permessi</h1>
          <p className="text-sm text-slate-500 mt-1">Registra assenze programmate e la loro disponibilità sui cantieri</p>
        </div>
        <Button className="bg-orange-500 hover:bg-orange-600 text-white h-12" onClick={() => setShowNew(true)} data-testid="add-leave-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuova richiesta
        </Button>
      </div>

      <Tabs defaultValue="calendario">
        <TabsList>
          <TabsTrigger value="calendario">Calendario</TabsTrigger>
          <TabsTrigger value="elenco">Elenco richieste</TabsTrigger>
          <TabsTrigger value="report">Riepilogo annuale</TabsTrigger>
        </TabsList>

        <TabsContent value="calendario">
          <LeaveCalendar requests={list}
                         onModifica={setEditRichiesta}
                         onPdf={downloadPdf}
                         onElimina={del} />
        </TabsContent>

        <TabsContent value="elenco" className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Cerca dipendente..." className="pl-9 h-11" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <select className="h-11 border border-slate-200 rounded px-3 text-sm" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="all">Tutte le tipologie</option>
              {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>

          {loading && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}
          {!loading && filtered.length === 0 && (
            <Card className="border-dashed border-2"><CardContent className="p-6 text-center text-slate-500">
              Nessuna richiesta registrata.
            </CardContent></Card>
          )}

          <div className="space-y-1.5">
            {filtered.map(r => {
              const meta = TYPE_META[r.leave_type] || TYPE_META.altro;
              const Icon = meta.icon;
              const days = Math.round((new Date(r.date_to) - new Date(r.date_from)) / 86400000) + 1;
              return (
                <Card key={r.id} className="border-slate-200">
                  <CardContent className="px-3 py-2 flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap leading-tight">
                        <span className="font-semibold text-sm">{r.employee_name}</span>
                        <Badge className={`${meta.color} border-0 text-[10px]`}>{meta.label}</Badge>
                        <span className="text-xs text-slate-500">
                          {r.parziale
                            ? <>{new Date(r.date_from).toLocaleDateString("it-IT")} · dalle {r.ora_inizio} alle {r.ora_fine}</>
                            : <>{new Date(r.date_from).toLocaleDateString("it-IT")} → {new Date(r.date_to).toLocaleDateString("it-IT")} · {days} giorn{days === 1 ? "o" : "i"}</>}
                        </span>
                        {r.description && <span className="text-xs text-slate-400 truncate">{r.description}</span>}
                      </div>
                      {r.modificata && (
                        <div className="text-[11px] text-amber-700 flex items-center gap-1 leading-tight">
                          <History className="w-3 h-3" />
                          Rettificata da {r.modifiche?.[r.modifiche.length - 1]?.by_name || "—"}
                          {" · "}{r.modifiche?.[r.modifiche.length - 1]?.motivo}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => setEditRichiesta(r)}
                              title="Modifica" data-testid={`edit-leave-${r.id}`}>
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => downloadPdf(r)} title="Scarica il PDF">
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 px-2 text-red-600" onClick={() => del(r.id)} title="Elimina">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="report">
          <RiepilogoAnnuale dati={summary} anno={annoRiepilogo} onAnno={setAnnoRiepilogo} />
        </TabsContent>
      </Tabs>

      {showNew && <NewLeaveDialog onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {editRichiesta && (
        <ModificaLeaveDialog richiesta={editRichiesta}
                             onClose={() => setEditRichiesta(null)}
                             onSaved={() => { setEditRichiesta(null); load(); }} />
      )}
    </div>
  );
}

// Nei giorni chiesti la persona è già in un cantiere? Si chiede al server
// mentre si compila, così l'avviso compare prima di firmare, non dopo.
function usePianificato(employeeId, dateFrom, dateTo) {
  const [giorni, setGiorni] = useState([]);
  useEffect(() => {
    if (!employeeId || !dateFrom || !dateTo || dateTo < dateFrom) { setGiorni([]); return; }
    let vivo = true;
    api.get("/leave-requests/pianificato", { params: { employee_id: employeeId, date_from: dateFrom, date_to: dateTo } })
      .then(({ data }) => { if (vivo) setGiorni(data.pianificato || []); })
      .catch(() => { if (vivo) setGiorni([]); });
    return () => { vivo = false; };
  }, [employeeId, dateFrom, dateTo]);
  return giorni;
}

const giornoIt = (iso) => (iso ? iso.substring(0, 10).split("-").reverse().join("/") : "");

function AvvisoPianificato({ giorni }) {
  if (!giorni.length) return null;
  return (
    <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800 space-y-1" data-testid="avviso-pianificato">
      <div className="font-semibold flex items-center gap-1">
        <AlertTriangle className="w-4 h-4" /> È già in squadra in {giorni.length} {giorni.length === 1 ? "giorno" : "giorni"} del periodo
      </div>
      <ul className="text-xs space-y-0.5">
        {giorni.slice(0, 8).map((g) => (
          <li key={g.date}>{giornoIt(g.date)} · {g.squadra}{g.cantiere ? ` · ${g.cantiere}` : ""}</li>
        ))}
        {giorni.length > 8 && <li>…e altri {giorni.length - 8}</li>}
      </ul>
      <div className="text-xs">La richiesta si registra lo stesso: poi va tolto dalla pianificazione di quei giorni.</div>
    </div>
  );
}

function NewLeaveDialog({ onClose, onSaved }) {
  const [employees, setEmployees] = useState([]);
  const [empSearch, setEmpSearch] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [leaveType, setLeaveType] = useState("ferie");
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().substring(0, 10));
  const [dateTo, setDateTo] = useState(new Date().toISOString().substring(0, 10));
  const [description, setDescription] = useState("");
  const [aOre, setAOre] = useState(false);
  const [oraInizio, setOraInizio] = useState("09:00");
  const [oraFine, setOraFine] = useState("13:00");
  const [busy, setBusy] = useState(false);
  const [firma, setFirma] = useState(null);
  const pianificato = usePianificato(employeeId, dateFrom, aOre ? dateFrom : dateTo);

  useEffect(() => {
    api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => {});
  }, []);


  const filteredEmp = employees.filter(e => !empSearch || e.full_name.toLowerCase().includes(empSearch.toLowerCase()));

  const submit = async () => {
    if (!employeeId) { toast.error("Seleziona un dipendente"); return; }
    if (leaveType === "altro" && !description.trim()) { toast.error("Indica una descrizione per 'Altro'"); return; }
    if (aOre && oraFine <= oraInizio) { toast.error("L'ora di fine deve essere successiva a quella di inizio"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("employee_id", employeeId);
    fd.append("leave_type", leaveType);
    fd.append("date_from", dateFrom);
    fd.append("date_to", aOre ? dateFrom : dateTo);
    if (aOre) { fd.append("ora_inizio", oraInizio); fd.append("ora_fine", oraFine); }
    if (description) fd.append("description", description);
    if (firma) fd.append("signature_base64", firma);
    try {
      const { data } = await api.post("/leave-requests", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.pianificato?.length) {
        toast.warning(`Registrata, ma è in squadra in ${data.pianificato.length} giorni del periodo: sistema la pianificazione`);
      } else {
        toast.success("Richiesta registrata");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Nuova richiesta ferie/permesso</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Dipendente</Label>
            {selectedEmp ? (
              <div className="flex items-center justify-between p-2 border border-orange-200 bg-orange-50 rounded mt-1">
                <span className="text-sm">{selectedEmp.full_name} · {selectedEmp.badge_number}</span>
                <Button type="button" variant="outline" size="sm" onClick={() => { setSelectedEmp(null); setEmployeeId(""); }}>Cambia</Button>
              </div>
            ) : (
              <>
                <Input placeholder="Cerca per nome..." className="mt-1" value={empSearch} onChange={(e) => setEmpSearch(e.target.value)} />
                {empSearch && (
                  <div className="border border-slate-200 rounded max-h-40 overflow-y-auto mt-1">
                    {filteredEmp.slice(0, 8).map(e => (
                      <button key={e.id} type="button"
                              onClick={() => { setEmployeeId(e.id); setSelectedEmp(e); setEmpSearch(""); }}
                              className="w-full text-left p-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0">
                        {e.full_name} · {e.badge_number}
                      </button>
                    ))}
                    {filteredEmp.length === 0 && <div className="p-2 text-xs text-slate-400">Nessun dipendente trovato</div>}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <Label>Tipologia</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {Object.entries(TYPE_META).map(([k, v]) => {
                const Icon = v.icon;
                const active = leaveType === k;
                return (
                  <button key={k} type="button" onClick={() => setLeaveType(k)}
                          className={`flex items-center gap-2 p-2.5 rounded border text-sm ${active ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200"}`}>
                    <Icon className="w-4 h-4" />{v.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data inizio</Label><Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); if (e.target.value > dateTo) setDateTo(e.target.value); }} /></div>
            <div><Label>Data fine</Label>
              <Input type="date" value={aOre ? dateFrom : dateTo} min={dateFrom} disabled={aOre}
                     onChange={(e) => setDateTo(e.target.value)} /></div>
          </div>

          {/* Permesso su una frazione di giornata: la persona resta
              assegnabile ai cantieri, ma con disponibilità parziale. */}
          <div className="border border-slate-200 rounded p-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={aOre} data-testid="permesso-a-ore"
                     onChange={(e) => { setAOre(e.target.checked); if (e.target.checked) setDateTo(dateFrom); }} />
              Solo per una parte della giornata
            </label>
            {aOre && (
              <>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div><Label>Dalle</Label>
                    <Input type="time" value={oraInizio} data-testid="ora-inizio"
                           onChange={(e) => setOraInizio(e.target.value)} /></div>
                  <div><Label>Alle</Label>
                    <Input type="time" value={oraFine} min={oraInizio} data-testid="ora-fine"
                           onChange={(e) => setOraFine(e.target.value)} /></div>
                </div>
                <p className="text-[10px] text-slate-500 mt-1.5">
                  Nella scelta delle risorse comparirà come disponibilità parziale, non come assenza.
                </p>
              </>
            )}
          </div>

          <div>
            <Label>Descrizione {leaveType === "altro" && <span className="text-red-500">*</span>}</Label>
            <textarea rows={2} className="w-full border border-slate-200 rounded px-3 py-2 text-sm mt-1"
                      placeholder={leaveType === "altro" ? "Motivo della richiesta..." : "Note aggiuntive (opzionale)"}
                      value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <div>
            <CampoFirma valore={firma} onChange={setFirma} etichetta="Firma del richiedente" />
          </div>
        </div>
        <AvvisoPianificato giorni={pianificato} />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
          <Button type="button" onClick={submit} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white">
            {busy ? "Registrazione..." : "Registra richiesta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const MESI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
              "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const GIORNI = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const isoOf = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * Calendario delle assenze: si naviga fra i mesi e selezionando una giornata
 * si vede subito chi sara' assente in quella data. Ogni riga porta gli stessi
 * tre tasti dell'elenco (modifica, PDF, elimina): trovata la persona sul
 * giorno, la si sistema da qui senza andare a ripescare la richiesta.
 */
function LeaveCalendar({ requests, onModifica, onPdf, onElimina }) {
  const oggi = new Date();
  const [cursor, setCursor] = useState(new Date(oggi.getFullYear(), oggi.getMonth(), 1));
  const [selected, setSelected] = useState(isoOf(oggi));

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // La settimana parte da lunedi': getDay() torna 0 per la domenica.
  const leadingBlanks = (first.getDay() + 6) % 7;

  const onDate = (iso) => (requests || []).filter(r => r.date_from <= iso && r.date_to >= iso);
  const selectedList = onDate(selected);

  const cells = [];
  for (let i = 0; i < leadingBlanks; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(isoOf(new Date(year, month, d)));

  const shiftMonth = (delta) => setCursor(new Date(year, month + delta, 1));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
      <Card className="border-slate-200 lg:col-span-2">
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <Button variant="outline" size="sm" onClick={() => shiftMonth(-1)} data-testid="cal-prev-month">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="text-center">
              <div className="font-display font-bold capitalize">{MESI[month]} {year}</div>
              <button type="button" className="text-[11px] text-orange-600 hover:underline"
                      onClick={() => { const n = new Date(); setCursor(new Date(n.getFullYear(), n.getMonth(), 1)); setSelected(isoOf(n)); }}>
                vai a oggi
              </button>
            </div>
            <Button variant="outline" size="sm" onClick={() => shiftMonth(1)} data-testid="cal-next-month">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-[11px] uppercase tracking-widest text-slate-400 mb-1">
            {GIORNI.map(g => <div key={g}>{g}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((iso, i) => {
              if (!iso) return <div key={`b${i}`} />;
              const assenti = onDate(iso);
              const isSel = iso === selected;
              const isToday = iso === isoOf(oggi);
              const weekend = [5, 6].includes((new Date(`${iso}T12:00:00`).getDay() + 6) % 7);
              return (
                <button key={iso} type="button" onClick={() => setSelected(iso)}
                        data-testid={`cal-day-${iso}`}
                        className={`h-12 rounded border px-1 py-0.5 flex flex-col items-center justify-start transition
                          ${isSel ? "border-orange-500 bg-orange-50 ring-2 ring-orange-200" : "border-slate-200 hover:border-slate-300"}
                          ${weekend && !isSel ? "bg-slate-50" : ""}`}>
                  <span className={`text-xs ${isToday ? "font-black text-orange-600" : "text-slate-600"}`}>
                    {Number(iso.substring(8, 10))}
                  </span>
                  {assenti.length > 0 && (
                    <>
                      <span className="flex gap-0.5 mt-0.5 flex-wrap justify-center">
                        {assenti.slice(0, 4).map(a => (
                          <span key={a.id} className={`w-1.5 h-1.5 rounded-full ${(TYPE_META[a.leave_type] || TYPE_META.altro).dot}`} />
                        ))}
                      </span>
                      <span className="text-[9px] text-slate-400 mt-auto">{assenti.length}</span>
                    </>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-3 mt-2 pt-2 border-t border-slate-100">
            {Object.entries(TYPE_META).map(([k, v]) => (
              <span key={k} className="flex items-center gap-1 text-[11px] text-slate-500">
                <span className={`w-2 h-2 rounded-full ${v.dot}`} /> {v.label}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-3">
          <h3 className="font-display font-bold flex items-center gap-2 mb-2">
            <CalendarDays className="w-4 h-4 text-orange-500" />
            {new Date(`${selected}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
          </h3>
          {selectedList.length === 0 ? (
            <p className="text-sm text-slate-500">Nessuna assenza registrata in questa giornata.</p>
          ) : (
            <div className="space-y-1.5">
              {selectedList.map(r => {
                const meta = TYPE_META[r.leave_type] || TYPE_META.altro;
                const Icon = meta.icon;
                return (
                  <div key={r.id} className="px-2 py-1.5 border border-slate-200 rounded"
                       data-testid={`cal-richiesta-${r.id}`}>
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate">{r.employee_name}</div>
                        <div className="text-[11px] text-slate-500">
                          {r.parziale
                            ? <>dalle {r.ora_inizio} alle {r.ora_fine}</>
                            : <>{new Date(r.date_from).toLocaleDateString("it-IT")} → {new Date(r.date_to).toLocaleDateString("it-IT")}</>}
                        </div>
                      </div>
                      <Badge className={`${meta.color} border-0 text-[10px] flex-shrink-0`}>{meta.label}</Badge>
                    </div>
                    {(onModifica || onPdf || onElimina) && (
                      <div className="flex gap-1 mt-1 justify-end">
                        {onModifica && (
                          <Button variant="outline" size="sm" className="h-7 px-2" title="Modifica"
                                  data-testid={`cal-edit-${r.id}`} onClick={() => onModifica(r)}>
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {onPdf && (
                          <Button variant="outline" size="sm" className="h-7 px-2" title="Scarica il PDF"
                                  data-testid={`cal-pdf-${r.id}`} onClick={() => onPdf(r)}>
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {onElimina && (
                          <Button variant="outline" size="sm" className="h-7 px-2 text-red-600" title="Elimina"
                                  data-testid={`cal-del-${r.id}`} onClick={() => onElimina(r.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Rettifica di una richiesta già registrata: un rientro anticipato, un
 * permesso che salta. Non si sovrascrive in silenzio — resta traccia di
 * com'era, del motivo e della firma di chi ha messo mano, perché un'assenza
 * registrata ha effetti su presenze e cantieri.
 */
function ModificaLeaveDialog({ richiesta, onClose, onSaved }) {
  const [leaveType, setLeaveType] = useState(richiesta.leave_type);
  const [dateFrom, setDateFrom] = useState((richiesta.date_from || "").substring(0, 10));
  const [dateTo, setDateTo] = useState((richiesta.date_to || "").substring(0, 10));
  const [aOre, setAOre] = useState(!!richiesta.parziale);
  const [oraInizio, setOraInizio] = useState(richiesta.ora_inizio || "09:00");
  const [oraFine, setOraFine] = useState(richiesta.ora_fine || "13:00");
  const [description, setDescription] = useState(richiesta.description || "");
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  const [firma, setFirma] = useState(null);
  const pianificato = usePianificato(richiesta.employee_id, dateFrom, aOre ? dateFrom : dateTo);


  const salva = async () => {
    if (!motivo.trim()) { toast.error("Indica il motivo della modifica"); return; }
    if (aOre && oraFine <= oraInizio) { toast.error("L'ora di fine deve essere successiva a quella di inizio"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("date_from", dateFrom);
    fd.append("date_to", aOre ? dateFrom : dateTo);
    fd.append("leave_type", leaveType);
    fd.append("description", description);
    fd.append("motivo", motivo.trim());
    if (aOre) { fd.append("ora_inizio", oraInizio); fd.append("ora_fine", oraFine); }
    if (firma) fd.append("signature_base64", firma);
    try {
      await api.put(`/leave-requests/${richiesta.id}`, fd,
                    { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Richiesta rettificata");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nella modifica"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Modifica richiesta · {richiesta.employee_name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
            Registrata in origine dal {new Date(richiesta.date_from).toLocaleDateString("it-IT")}
            {" al "}{new Date(richiesta.date_to).toLocaleDateString("it-IT")}.
            La versione precedente resta conservata.
          </p>

          <div>
            <Label>Tipologia</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {Object.entries(TYPE_META).map(([k, v]) => {
                const Icon = v.icon;
                return (
                  <button key={k} type="button" onClick={() => setLeaveType(k)}
                          className={`flex items-center gap-2 p-2.5 rounded border text-sm ${
                            leaveType === k ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200"}`}>
                    <Icon className="w-4 h-4" />{v.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data inizio</Label>
              <Input type="date" value={dateFrom}
                     onChange={(e) => { setDateFrom(e.target.value); if (e.target.value > dateTo) setDateTo(e.target.value); }} /></div>
            <div><Label>Data fine</Label>
              <Input type="date" value={aOre ? dateFrom : dateTo} min={dateFrom} disabled={aOre}
                     data-testid="modifica-data-fine"
                     onChange={(e) => setDateTo(e.target.value)} /></div>
          </div>

          <div className="border border-slate-200 rounded p-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={aOre}
                     onChange={(e) => { setAOre(e.target.checked); if (e.target.checked) setDateTo(dateFrom); }} />
              Solo per una parte della giornata
            </label>
            {aOre && (
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div><Label>Dalle</Label>
                  <Input type="time" value={oraInizio} onChange={(e) => setOraInizio(e.target.value)} /></div>
                <div><Label>Alle</Label>
                  <Input type="time" value={oraFine} min={oraInizio} onChange={(e) => setOraFine(e.target.value)} /></div>
              </div>
            )}
          </div>

          <div>
            <Label>Motivo della modifica <span className="text-red-500">*</span></Label>
            <Input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                   placeholder="es. rientrato un giorno prima" className="mt-1"
                   data-testid="modifica-motivo" />
          </div>

          <div><Label>Note</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} /></div>

          <div>
            <CampoFirma valore={firma} onChange={setFirma} etichetta="Firma di chi registra la modifica" />
            <p className="text-[10px] text-slate-500 mt-1">
              Compare sul PDF accanto alla rettifica, insieme al motivo e al periodo precedente.
            </p>
          </div>

          {richiesta.modifiche?.length > 0 && (
            <div className="text-xs text-slate-500 border-t border-slate-100 pt-2">
              <div className="font-semibold mb-1">Modifiche precedenti</div>
              {richiesta.modifiche.map((m, i) => (
                <div key={i}>
                  {new Date(m.at).toLocaleDateString("it-IT")} · {m.by_name || m.by} · {m.motivo}
                </div>
              ))}
            </div>
          )}
        </div>
        <AvvisoPianificato giorni={pianificato} />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
          <Button type="button" onClick={salva} disabled={busy} data-testid="modifica-salva"
                  className="bg-orange-500 hover:bg-orange-600 text-white">
            {busy ? "Salvataggio..." : "Salva rettifica"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Il riepilogo dell'anno: i totali per tipologia, la tabella per dipendente
 * che si ordina cliccando le intestazioni, il mese per mese, e l'Excel con
 * tutto (compresa la lista delle richieste una per riga).
 */
const COLONNE_RIEPILOGO = [
  { chiave: "employee_name", titolo: "Dipendente", testo: true },
  { chiave: "ferie_giorni", titolo: "Ferie" },
  { chiave: "permesso_giorni", titolo: "Permessi" },
  { chiave: "permesso_104_giorni", titolo: "Legge 104" },
  { chiave: "malattia_giorni", titolo: "Malattia" },
  { chiave: "altro_giorni", titolo: "Altro" },
  { chiave: "totale_giorni", titolo: "Totale gg" },
  { chiave: "richieste", titolo: "Richieste" },
  { chiave: "ore_permesso", titolo: "Ore perm." },
];
const TINTE_TIPO = { ferie: "#10b981", permesso: "#3b82f6", permesso_104: "#a855f7", malattia: "#ef4444", altro: "#94a3b8" };

function RiepilogoAnnuale({ dati, anno, onAnno }) {
  const [ordine, setOrdine] = useState({ chiave: "employee_name", verso: 1 });
  const [cerca, setCerca] = useState("");
  const [vista, setVista] = useState("tipologie");

  const righe = useMemo(() => {
    if (!dati?.righe) return [];
    const q = cerca.trim().toLowerCase();
    const col = COLONNE_RIEPILOGO.find(c => c.chiave === ordine.chiave) || COLONNE_RIEPILOGO[0];
    return dati.righe
      .filter(r => !q || r.employee_name.toLowerCase().includes(q))
      .slice()
      .sort((a, b) => {
        const va = a[col.chiave], vb = b[col.chiave];
        const d = col.testo ? String(va).localeCompare(String(vb), "it") : (Number(va) || 0) - (Number(vb) || 0);
        // a parita' si tiene l'ordine alfabetico, cosi' la lista non balla
        return (d || a.employee_name.localeCompare(b.employee_name, "it")) * ordine.verso;
      });
  }, [dati, cerca, ordine]);

  const ordinaPer = (chiave) => setOrdine(prev =>
    prev.chiave === chiave ? { chiave, verso: -prev.verso }
      : { chiave, verso: chiave === "employee_name" ? 1 : -1 });

  const scaricaExcel = async () => {
    try {
      const r = await authFetch(`/leave-requests/summary/excel?year=${anno}`);
      if (!r.ok) throw new Error();
      const u = window.URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u; a.download = `riepilogo_assenze_${anno}.xlsx`; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Errore nel download dell'Excel"); }
  };

  if (!dati) return <p className="text-sm text-slate-500 py-6 text-center">Caricamento...</p>;
  const tot = dati.totali || {};
  const media = tot.dipendenti ? (tot.ferie_giorni / tot.dipendenti).toFixed(1) : "0";
  const tessere = [
    { titolo: "Ferie", valore: tot.ferie_giorni, colore: "text-emerald-700", sfondo: "bg-emerald-50" },
    { titolo: "Permessi", valore: tot.permesso_giorni, colore: "text-blue-700", sfondo: "bg-blue-50" },
    { titolo: "Legge 104", valore: tot.permesso_104_giorni, colore: "text-purple-700", sfondo: "bg-purple-50" },
    { titolo: "Malattia", valore: tot.malattia_giorni, colore: "text-red-700", sfondo: "bg-red-50" },
    { titolo: "Altro", valore: tot.altro_giorni, colore: "text-slate-700", sfondo: "bg-slate-50" },
    { titolo: "Richieste", valore: tot.richieste, colore: "text-slate-900", sfondo: "bg-white" },
    { titolo: "Dipendenti", valore: tot.dipendenti, colore: "text-slate-900", sfondo: "bg-white" },
    { titolo: "Media ferie / dip.", valore: media, colore: "text-slate-900", sfondo: "bg-white" },
  ];
  const Freccia = ({ chiave }) => ordine.chiave !== chiave
    ? <ArrowUpDown className="w-3 h-3 inline-block ml-1 text-slate-300" />
    : ordine.verso === 1 ? <ArrowUp className="w-3 h-3 inline-block ml-1 text-orange-500" />
      : <ArrowDown className="w-3 h-3 inline-block ml-1 text-orange-500" />;

  return (
    <div className="space-y-3" data-testid="riepilogo-annuale">
      <div className="flex gap-2 flex-wrap items-center">
        <select className="h-9 border border-slate-200 rounded px-2 text-sm" value={anno}
                onChange={(e) => onAnno(e.target.value)} data-testid="riepilogo-anno">
          {(dati.anni || [anno]).map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Cerca dipendente..." className="pl-9 h-9" value={cerca} onChange={(e) => setCerca(e.target.value)} />
        </div>
        <div className="flex rounded border border-slate-200 overflow-hidden text-xs">
          {[["tipologie", "Per tipologia"], ["mesi", "Per mese"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setVista(k)}
                    className={`px-3 h-9 ${vista === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              {l}
            </button>
          ))}
        </div>
        <Button variant="outline" className="h-9" onClick={scaricaExcel} data-testid="riepilogo-excel">
          <FileSpreadsheet className="w-4 h-4 mr-2" /> Esporta Excel
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        {tessere.map(x => (
          <div key={x.titolo} className={`rounded border border-slate-200 px-3 py-2 ${x.sfondo}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 truncate">{x.titolo}</div>
            <div className={`font-display text-2xl font-black leading-tight ${x.colore}`}>{x.valore ?? 0}</div>
          </div>
        ))}
      </div>

      {vista === "tipologie" && (
        <Card className="border-slate-200">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  {COLONNE_RIEPILOGO.map(c => (
                    <th key={c.chiave} onClick={() => ordinaPer(c.chiave)} title="Clicca per ordinare"
                        className={`px-3 py-2 cursor-pointer select-none whitespace-nowrap hover:text-slate-800 ${c.testo ? "text-left" : "text-right"}`}>
                      {c.titolo}<Freccia chiave={c.chiave} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {righe.length === 0 && (
                  <tr><td colSpan={COLONNE_RIEPILOGO.length} className="text-center p-6 text-slate-400">Nessun dato per l&apos;anno scelto</td></tr>
                )}
                {righe.map(s => (
                  <tr key={s.employee_id} className="border-t border-slate-100 hover:bg-orange-50/40">
                    {COLONNE_RIEPILOGO.map(c => (
                      <td key={c.chiave} className={`px-3 py-1.5 ${c.testo ? "font-semibold" : "text-right tabular-nums"} ${
                        !c.testo && !s[c.chiave] ? "text-slate-300" : ""}`}>
                        {s[c.chiave] ?? 0}
                      </td>
                    ))}
                  </tr>
                ))}
                {righe.length > 0 && (
                  <tr className="border-t-2 border-slate-200 bg-slate-50 font-bold">
                    <td className="px-3 py-1.5">Totale</td>
                    {COLONNE_RIEPILOGO.slice(1).map(c => (
                      <td key={c.chiave} className="px-3 py-1.5 text-right tabular-nums">
                        {Math.round(righe.reduce((acc, r) => acc + (Number(r[c.chiave]) || 0), 0) * 100) / 100}
                      </td>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {vista === "mesi" && (
        <div className="space-y-3">
          <Card className="border-slate-200">
            <CardContent className="p-3">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Giorni di assenza per mese e tipologia</div>
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dati.per_mese} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <XAxis dataKey="nome" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {(dati.tipi || []).map(tp => (
                      <Bar key={tp.value} dataKey={tp.value} name={tp.label} stackId="a" fill={TINTE_TIPO[tp.value] || "#94a3b8"} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th onClick={() => ordinaPer("employee_name")} className="px-3 py-2 text-left cursor-pointer select-none">
                      Dipendente<Freccia chiave="employee_name" />
                    </th>
                    {(dati.per_mese || []).map(m => <th key={m.mese} className="px-2 py-2 text-right">{m.nome}</th>)}
                    <th onClick={() => ordinaPer("totale_giorni")} className="px-3 py-2 text-right cursor-pointer select-none">
                      Totale<Freccia chiave="totale_giorni" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {righe.map(s => (
                    <tr key={s.employee_id} className="border-t border-slate-100 hover:bg-orange-50/40">
                      <td className="px-3 py-1.5 font-semibold whitespace-nowrap">{s.employee_name}</td>
                      {s.mesi.map((g, i) => (
                        <td key={i} className={`px-2 py-1.5 text-right tabular-nums ${g ? "" : "text-slate-300"}`}>{g}</td>
                      ))}
                      <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{s.totale_giorni}</td>
                    </tr>
                  ))}
                  {righe.length > 0 && (
                    <tr className="border-t-2 border-slate-200 bg-slate-50 font-bold">
                      <td className="px-3 py-1.5">Totale</td>
                      {(dati.per_mese || []).map((m, i) => (
                        <td key={m.mese} className="px-2 py-1.5 text-right tabular-nums">
                          {righe.reduce((acc, r) => acc + (r.mesi[i] || 0), 0)}
                        </td>
                      ))}
                      <td className="px-3 py-1.5 text-right tabular-nums">{righe.reduce((acc, r) => acc + r.totale_giorni, 0)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
